/**
 * Text rendering engine for uchiwa canvas.
 * Supports horizontal and vertical text layout with auto-sizing.
 */

// Characters that need 90° rotation in vertical mode
const ROTATE_CHARS = new Set([
  '（', '）', '(', ')', '「', '」', '『', '』',
  '＜', '＞', '<', '>',
]);

// Punctuation that shifts position in vertical mode
const PUNCT_SHIFT: Record<string, { dx: number; dy: number; rotate?: number }> = {
  '。': { dx: 0.3, dy: -0.3 },
  '、': { dx: 0.3, dy: -0.3 },
  '！': { dx: 0, dy: 0 },
  '？': { dx: 0, dy: 0 },
  '!': { dx: 0, dy: 0 },
  '?': { dx: 0, dy: 0 },
};

// Small kana that shift up-right in vertical mode
const SMALL_KANA = new Set([
  'っ', 'ゃ', 'ゅ', 'ょ', 'ぁ', 'ぃ', 'ぅ', 'ぇ', 'ぉ',
  'ッ', 'ャ', 'ュ', 'ョ', 'ァ', 'ィ', 'ゥ', 'ェ', 'ォ',
]);

// Long vowel mark replacement in vertical mode
const VERTICAL_REPLACE: Record<string, string> = {
  'ー': '｜',
  '…': '⋮',
  '〜': '∣',
};

// Latin characters that get rotated 90° in vertical
function isLatinOrDigit(ch: string): boolean {
  return /^[A-Za-z0-9]$/.test(ch);
}

export interface TextLayoutOptions {
  text: string;
  orientation: 'horizontal' | 'vertical';
  fontFamily: string;
  fontWeight: string;
  /** Available drawing area width */
  areaWidth: number;
  /** Available drawing area height */
  areaHeight: number;
  /** Area center X */
  centerX: number;
  /** Area center Y */
  centerY: number;
}

export interface CharPosition {
  char: string;
  x: number;
  y: number;
  fontSize: number;
  rotate?: number; // radians
}

/**
 * Compute optimal font size so that all characters fit in the area.
 * For horizontal: text width fits areaWidth, text height fits areaHeight.
 * For vertical: text height fits areaHeight, text width fits areaWidth.
 */
export function computeFontSize(
  ctx: CanvasRenderingContext2D,
  text: string,
  orientation: 'horizontal' | 'vertical',
  fontFamily: string,
  fontWeight: string,
  areaWidth: number,
  areaHeight: number,
): number {
  const charCount = text.length;
  if (charCount === 0) return 60;

  // Padding factor to keep text from touching edges
  const pad = 0.85;

  if (orientation === 'horizontal') {
    // Start with a size that fills the height
    let fontSize = areaHeight * pad * 0.8;
    // Check if it fits horizontally
    for (let i = 0; i < 10; i++) {
      ctx.font = `${fontWeight} ${fontSize}px '${fontFamily}', sans-serif`;
      const metrics = ctx.measureText(text);
      const textWidth = metrics.width;
      if (textWidth > areaWidth * pad) {
        fontSize *= (areaWidth * pad) / textWidth;
      } else {
        break;
      }
    }
    // Cap at area height
    if (fontSize > areaHeight * pad * 0.85) {
      fontSize = areaHeight * pad * 0.85;
    }
    return Math.floor(fontSize);
  } else {
    // Vertical: each char stacked, total height = charCount * charSize * spacing
    const spacing = 1.1;
    let fontSize = (areaHeight * pad) / (charCount * spacing);
    // Cap at area width
    if (fontSize > areaWidth * pad * 0.85) {
      fontSize = areaWidth * pad * 0.85;
    }
    // Minimum size
    if (fontSize < 20) fontSize = 20;
    return Math.floor(fontSize);
  }
}

/**
 * Layout characters for rendering. Returns position info for each character.
 */
export function layoutText(
  ctx: CanvasRenderingContext2D,
  opts: TextLayoutOptions,
): CharPosition[] {
  const { text, orientation, fontFamily, fontWeight, areaWidth, areaHeight, centerX, centerY } = opts;

  if (!text) return [];

  const fontSize = computeFontSize(ctx, text, orientation, fontFamily, fontWeight, areaWidth, areaHeight);
  const chars = [...text]; // Handle surrogate pairs

  if (orientation === 'horizontal') {
    return layoutHorizontal(ctx, chars, fontSize, fontFamily, fontWeight, centerX, centerY);
  } else {
    return layoutVertical(ctx, chars, fontSize, fontFamily, fontWeight, centerX, centerY, areaHeight);
  }
}

function layoutHorizontal(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  fontSize: number,
  fontFamily: string,
  fontWeight: string,
  centerX: number,
  centerY: number,
): CharPosition[] {
  // Simple: all chars at same position, rendered as single string
  // But we return per-char for style flexibility
  ctx.font = `${fontWeight} ${fontSize}px '${fontFamily}', sans-serif`;
  const fullText = chars.join('');
  const totalWidth = ctx.measureText(fullText).width;

  let x = centerX - totalWidth / 2;
  const result: CharPosition[] = [];

  for (const ch of chars) {
    const charWidth = ctx.measureText(ch).width;
    result.push({
      char: ch,
      x: x + charWidth / 2,
      y: centerY,
      fontSize,
    });
    x += charWidth;
  }

  return result;
}

function layoutVertical(
  _ctx: CanvasRenderingContext2D,
  chars: string[],
  fontSize: number,
  _fontFamily: string,
  _fontWeight: string,
  centerX: number,
  centerY: number,
  _areaHeight: number,
): CharPosition[] {
  const spacing = fontSize * 1.15;
  const totalHeight = chars.length * spacing;
  let y = centerY - totalHeight / 2 + spacing / 2;

  const result: CharPosition[] = [];

  for (const ch of chars) {
    let displayChar = VERTICAL_REPLACE[ch] ?? ch;
    let rotate: number | undefined;
    let dx = 0;
    let dy = 0;

    if (ROTATE_CHARS.has(ch)) {
      rotate = Math.PI / 2;
    } else if (isLatinOrDigit(ch)) {
      // Rotate latin chars 90° clockwise in vertical mode
      rotate = Math.PI / 2;
    } else if (SMALL_KANA.has(ch)) {
      dx = fontSize * 0.12;
      dy = -fontSize * 0.12;
    } else if (PUNCT_SHIFT[ch]) {
      const shift = PUNCT_SHIFT[ch];
      dx = shift.dx * fontSize;
      dy = shift.dy * fontSize;
      if (shift.rotate !== undefined) rotate = shift.rotate;
    }

    result.push({
      char: displayChar,
      x: centerX + dx,
      y: y + dy,
      fontSize,
      rotate,
    });

    y += spacing;
  }

  return result;
}

/**
 * Draw laid-out characters onto canvas context.
 * This is the "simple" drawing - styles will override with their own effects.
 */
export function drawChars(
  ctx: CanvasRenderingContext2D,
  positions: CharPosition[],
  fontFamily: string,
  fontWeight: string,
  fillStyle: string | CanvasGradient,
): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const pos of positions) {
    ctx.save();
    ctx.font = `${fontWeight} ${pos.fontSize}px '${fontFamily}', sans-serif`;
    ctx.fillStyle = fillStyle;
    ctx.translate(pos.x, pos.y);
    if (pos.rotate) {
      ctx.rotate(pos.rotate);
    }
    ctx.fillText(pos.char, 0, 0);
    ctx.restore();
  }
}

/**
 * Stroke laid-out characters onto canvas context.
 */
export function strokeChars(
  ctx: CanvasRenderingContext2D,
  positions: CharPosition[],
  fontFamily: string,
  fontWeight: string,
  strokeStyle: string,
  lineWidth: number,
): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';

  for (const pos of positions) {
    ctx.save();
    ctx.font = `${fontWeight} ${pos.fontSize}px '${fontFamily}', sans-serif`;
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    ctx.translate(pos.x, pos.y);
    if (pos.rotate) {
      ctx.rotate(pos.rotate);
    }
    ctx.strokeText(pos.char, 0, 0);
    ctx.restore();
  }
}
